/**
 * Processing/progress observation for a media page — pure Layer 2 hook.
 *
 * Reads the enriched content (with `output_state` metadata) and Camunda
 * variables to produce ONLY progress-display data. Content-state facts
 * (failed / completed / no-source-coming) are derived directly from
 * `output_state` in the view — not here.
 *
 * Two information sources for display:
 *   - Camunda process variables → "how far along?" (live progress %)
 *   - output_state metadata      → "is it done?" (to hide finished bars)
 *
 * Camunda variables are polled for display while non-terminal items exist.
 * This is a one-way read — no feedback loop, no content emission.
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUIDVariablesPoll } from '@package/helpers/useUIDVariablesPoll';
import {
  deriveTerminalProgressKeys,
  isTerminalState,
  outputStateOf,
  parseProgressVariables,
  deriveGenerationStatus,
} from 'volto-base-addon/helpers/mediaProcessingLogic';
import {
  subtitleGenerationCleared,
  GENERATION_TIMEOUT_MS,
} from 'volto-base-addon/reducers/subtitleGeneration';

const PROGRESS_VARIABLES = [
  'transcodingProgress',
  'transcriptionProgress',
  'duration',
  'isProcessing',
  'faceblurProgress',
];

export const useMediaProcessing = (content, { isLiveStream } = {}) => {
  const dispatch = useDispatch();

  // content can be {} mid-navigation.
  const items = Array.isArray(content?.items) ? content.items : [];
  const uid = content?.UID;

  // Terminal Camunda progress keys (task/profile) — used only to hide the bars
  // of work that is already done. Derived from output_state metadata.
  const { completedItems, failedItems } = deriveTerminalProgressKeys(items);

  const progressItems = items.filter(
    (item) => item['@type'] === 'transcoding' || item['@type'] === 'subtitle',
  );

  // Poll Camunda while any progress item is non-terminal. No non-terminal
  // items (a settled page) = no polling.
  const hasNonTerminalItems =
    !!uid &&
    progressItems.length > 0 &&
    progressItems.some((item) => !isTerminalState(outputStateOf(item)));

  // Generation bridge: surfaces the "pending" / "timed_out" status for the
  // view to show feedback during the gap between Generate and child appearance.
  const generation = useSelector((state) => state.subtitleGeneration);
  const generationStatus = deriveGenerationStatus({
    generationUid: generation?.uid,
    generationStartedAt: generation?.startedAt,
    timeoutMs: GENERATION_TIMEOUT_MS,
    contentUid: uid,
    normalPollWouldRun: hasNonTerminalItems,
  });

  // Self-clear: the moment non-terminal items appear, the generation signal
  // is redundant. Clear it so it doesn't linger across navigations.
  useEffect(() => {
    if (generation?.uid && hasNonTerminalItems) {
      dispatch(subtitleGenerationCleared());
    }
  }, [generation?.uid, hasNonTerminalItems, dispatch]);

  const generationPending = generationStatus === 'pending';
  const progressShouldPoll =
    !isLiveStream && (hasNonTerminalItems || generationPending);

  const progressVariables = useUIDVariablesPoll(
    5000,
    uid,
    PROGRESS_VARIABLES,
    progressShouldPoll,
  );

  const {
    transcodingProgress,
    transcriptionProgress,
    transcriptionEntries,
    isProcessing,
    faceblurProgress,
  } = parseProgressVariables(progressVariables, completedItems, failedItems);

  return {
    transcodingProgress,
    transcriptionProgress,
    transcriptionEntries,
    isProcessing,
    faceblurProgress,
    generationStatus,
  };
};

export default useMediaProcessing;
